import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { COMPANY, SERVICES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  full_name: "",
  email: "",
  phone: "",
  service_type: SERVICES[0].title,
  property_type: "House",
  message: "",
};

const FIELD =
  "w-full bg-white border border-black/15 rounded-xl px-4 py-3.5 text-sm text-[#121212] placeholder:text-neutral-400 transition-colors";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);

      const subject = encodeURIComponent(`New Enquiry — ${form.service_type} — ${form.full_name}`);
      const body = encodeURIComponent(
        `Name: ${form.full_name}\nEmail: ${form.email}\nPhone: ${form.phone}\nService: ${form.service_type}\nProperty: ${form.property_type}\n\nMessage:\n${form.message}`
      );
      window.location.href = `mailto:${COMPANY.email}?subject=${subject}&body=${body}`;

      toast.success("Enquiry sent! Your email app is opening so it also lands in our inbox.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong — please call us directly on " + COMPANY.phone1);
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-20">
      <Seo
        title="Contact Us — Eunice 25 | Free Painting Quotes in Oldham"
        siteName="Eunice 25"
        description="Get a free painting quote from Eunice 25. Call +44 7474 891884, email owoeunice@yahoo.com or visit 4 Derby Street, Werneth, Oldham OL9 7BW."
      />

      <section className="bg-[#121212] text-[#fbf9f5] grain relative py-24 sm:py-32" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-[#c8102e]">Contact Us</p>
            <h1 className="mt-4 font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
              Let's talk about <span className="italic text-[#c8102e]">your home.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-[#fbf9f5]/70 leading-relaxed">
              Free quotes, friendly advice and honest prices. Send the form below or reach us
              directly — we usually reply the same day.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#fbf9f5] py-20 sm:py-28" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-10">
          {/* FORM */}
          <Reveal className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-white border border-black/10 rounded-2xl p-7 sm:p-10 shadow-sm"
            >
              <h2 className="font-display font-bold text-2xl tracking-tight">Request a Free Quote</h2>
              <p className="mt-1 text-sm text-neutral-500">Fields marked * are required.</p>

              <div className="mt-8 grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="full_name" className="block text-sm font-bold mb-2">Full Name *</label>
                  <input id="full_name" data-testid="form-full-name" required minLength={2}
                    value={form.full_name} onChange={set("full_name")} placeholder="Jane Smith" className={FIELD} />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-bold mb-2">Phone *</label>
                  <input id="phone" data-testid="form-phone" required minLength={5} type="tel"
                    value={form.phone} onChange={set("phone")} placeholder="07000 000000" className={FIELD} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="email" className="block text-sm font-bold mb-2">Email *</label>
                  <input id="email" data-testid="form-email" required type="email"
                    value={form.email} onChange={set("email")} placeholder="jane@example.com" className={FIELD} />
                </div>
                <div>
                  <label htmlFor="service_type" className="block text-sm font-bold mb-2">Service Needed *</label>
                  <select id="service_type" data-testid="form-service-type" value={form.service_type}
                    onChange={set("service_type")} className={FIELD}>
                    {SERVICES.map((s) => <option key={s.id} value={s.title}>{s.title}</option>)}
                    <option value="Full Home Refresh">Full Home Refresh</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="property_type" className="block text-sm font-bold mb-2">Property Type</label>
                  <select id="property_type" data-testid="form-property-type" value={form.property_type}
                    onChange={set("property_type")} className={FIELD}>
                    <option>House</option>
                    <option>Flat / Apartment</option>
                    <option>Bungalow</option>
                    <option>Commercial</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="message" className="block text-sm font-bold mb-2">Tell Us About the Job *</label>
                  <textarea id="message" data-testid="form-message" required minLength={5} rows={5}
                    value={form.message} onChange={set("message")}
                    placeholder="e.g. Two bedrooms and a hallway need repainting, including ceilings and skirting boards…"
                    className={`${FIELD} resize-none`} />
                </div>
              </div>

              <button
                type="submit"
                data-testid="form-submit-btn"
                disabled={sending}
                className="mt-8 w-full sm:w-auto flex items-center justify-center gap-2 bg-[#c8102e] hover:bg-[#a00d24] disabled:opacity-60 text-white font-bold px-10 py-4 rounded-full transition-colors"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? "Sending…" : "Send Enquiry"}
              </button>
            </form>
          </Reveal>

          {/* INFO CARD */}
          <Reveal delay={0.15} className="lg:col-span-2">
            <aside className="space-y-6">
              <div className="bg-[#121212] text-[#fbf9f5] rounded-2xl p-8 grain relative" data-testid="contact-info-card">
                <div className="flex items-center gap-3">
                  <img src="/assets/logo.png" alt="Eunice 25 logo" className="h-14 w-auto object-contain" />
                  <span className="font-display font-black text-xl">
                    EUNICE <span className="text-[#c8102e]">25</span>
                  </span>
                </div>
                <p className="mt-3 font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#fbf9f5]/50">
                  {COMPANY.founders} · CEO
                </p>
                <ul className="mt-8 space-y-5 text-sm">
                  <li>
                    <a href={COMPANY.phone1Href} data-testid="contact-phone-1" className="flex items-center gap-3 hover:text-[#c8102e] transition-colors">
                      <Phone className="w-5 h-5 text-[#c8102e]" /> {COMPANY.phone1}
                    </a>
                  </li>
                  <li>
                    <a href={COMPANY.phone2Href} data-testid="contact-phone-2" className="flex items-center gap-3 hover:text-[#c8102e] transition-colors">
                      <Phone className="w-5 h-5 text-[#c8102e]" /> {COMPANY.phone2}
                    </a>
                  </li>
                  <li>
                    <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="flex items-center gap-3 hover:text-[#c8102e] transition-colors break-all">
                      <Mail className="w-5 h-5 text-[#c8102e] shrink-0" /> {COMPANY.email}
                    </a>
                  </li>
                  <li className="flex items-start gap-3 text-[#fbf9f5]/80">
                    <MapPin className="w-5 h-5 text-[#c8102e] shrink-0 mt-0.5" />
                    <span data-testid="contact-address">{COMPANY.address}</span>
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl overflow-hidden border border-black/10" data-testid="contact-map">
                <iframe
                  title="Eunice 25 location — Werneth, Oldham"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=-2.1550%2C53.5250%2C-2.0750%2C53.5650&layer=mapnik&marker=53.5450%2C-2.1150"
                  className="w-full h-64"
                  loading="lazy"
                />
              </div>
            </aside>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
